import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/data';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminOperations.css';

const AdminOperations = () => {
  const navigate = useNavigate();
  const [operations, setOperations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [showOperationDetails, setShowOperationDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: '',
    currency: 'RUB',
    status: 'created',
    comment: '',
    admin_comment: '',
    contact_method: '',
    recipient_details: {}
  });
  const [processingOperation, setProcessingOperation] = useState(null);

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchOperations();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate, currentPage, statusFilter, typeFilter]);

  const fetchOperations = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(typeFilter !== 'all' && { type: typeFilter }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(`${API_URL}/api/admin/operations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOperations(response.data.operations || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching operations:', error);
      setError('Ошибка загрузки операций');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOperations();
  };

  const handleStatusUpdate = async (operationId, newStatus) => {
    try {
      setProcessingOperation(operationId);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/admin/operations/${operationId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchOperations();
    } catch (error) {
      console.error('Error updating operation status:', error);
      setError('Ошибка обновления статуса операции');
    } finally {
      setProcessingOperation(null);
    }
  };

  const handleProcessDeposit = async (operationId) => {
    try {
      setProcessingOperation(operationId);
      const token = localStorage.getItem('token');
      
      await axios.post(`${API_URL}/api/admin/operations/${operationId}/process-deposit`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchOperations();
    } catch (error) {
      console.error('Error processing deposit:', error);
      setError('Ошибка обработки пополнения');
    } finally {
      setProcessingOperation(null);
    }
  };

  const handleViewDetails = (operation) => {
    setSelectedOperation(operation);
    setShowOperationDetails(true);
  };

  const handleEditOperation = (operation) => {
    setEditingOperation(operation);
    setEditForm({
      amount: operation.amount || '',
      currency: operation.currency || 'RUB',
      status: operation.status || 'created',
      comment: operation.comment || '',
      admin_comment: operation.admin_comment || '',
      contact_method: operation.contact_method || '',
      recipient_details: operation.recipient_details || {}
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveOperation = async () => {
    try {
      setProcessingOperation(editingOperation.id);
      const token = localStorage.getItem('token');
      
      const updateData = {};
      
      // Добавляем только измененные поля
      if (editForm.amount !== editingOperation.amount) {
        updateData.amount = parseFloat(editForm.amount);
      }
      if (editForm.currency !== editingOperation.currency) {
        updateData.currency = editForm.currency;
      }
      if (editForm.status !== editingOperation.status) {
        updateData.status = editForm.status;
      }
      if (editForm.comment !== (editingOperation.comment || '')) {
        updateData.comment = editForm.comment;
      }
      if (editForm.admin_comment !== (editingOperation.admin_comment || '')) {
        updateData.admin_comment = editForm.admin_comment;
      }
      if (editForm.contact_method !== (editingOperation.contact_method || '')) {
        updateData.contact_method = editForm.contact_method;
      }
      if (JSON.stringify(editForm.recipient_details) !== JSON.stringify(editingOperation.recipient_details || {})) {
        updateData.recipient_details = editForm.recipient_details;
      }

      await axios.put(`${API_URL}/api/admin/operations/${editingOperation.id}`, 
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowEditModal(false);
      setEditingOperation(null);
      await fetchOperations();
    } catch (error) {
      console.error('Error updating operation:', error);
      setError('Ошибка обновления операции');
    } finally {
      setProcessingOperation(null);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingOperation(null);
    setEditForm({
      amount: '',
      currency: 'RUB',
      status: 'created',
      comment: '',
      admin_comment: '',
      contact_method: '',
      recipient_details: {}
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'RUB') => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'created': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'rejected': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'created': return 'Создана';
      case 'processing': return 'Обрабатывается';
      case 'completed': return 'Завершена';
      case 'rejected': return 'Отклонена';
      default: return status;
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'deposit': return 'Пополнение';
      case 'withdrawal': return 'Вывод';
      default: return type;
    }
  };

  const getAccountTypeText = (accountType) => {
    switch (accountType) {
      case 'banking': return 'Банковский';
      case 'trading': return 'Торговый';
      default: return 'Неизвестно';
    }
  };

  if (isLoading) {
    return (
      <div className="admin-operations">
        <div className="admin-header">
          <div className="admin-header__logo">
            <img src="/assets/img/logo.svg" alt="Pro Investor" />
          </div>
          <div className="admin-header__title">Управление операциями</div>
          <div className="admin-header__actions">
            <button className="admin-header__back" onClick={() => navigate('/admin')}>
              <span className="--icon-arrow-left"></span>
              Назад
            </button>
            <button className="admin-header__logout" onClick={() => logoutAdmin()}>
              <span className="--icon-log-out"></span>
              Выйти
            </button>
          </div>
        </div>
        <div className="admin-loading">
          <div className="admin-loading__spinner"></div>
          <p>Загрузка операций...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-operations">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Управление операциями</div>
        <div className="admin-header__actions">
          <button className="admin-header__back" onClick={() => navigate('/admin')}>
            <span className="--icon-arrow-left"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={() => logoutAdmin()}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-content__header">
          <h1 className="admin-title">Операции</h1>
          <button className="admin-refresh-btn" onClick={fetchOperations}>
            <span className="--icon-arrow"></span>
            Обновить
          </button>
        </div>

        {/* Filters */}
        <div className="admin-filters">
          <form onSubmit={handleSearch} className="admin-search">
            <input
              type="text"
              placeholder="Поиск по ID, имени, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-search__input"
            />
            <button type="submit" className="admin-search__btn">
              <span className="--icon-search"></span>
            </button>
          </form>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-filter__select"
          >
            <option value="all">Все статусы</option>
            <option value="created">Создана</option>
            <option value="processing">Обрабатывается</option>
            <option value="completed">Завершена</option>
            <option value="rejected">Отклонена</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="admin-filter__select"
          >
            <option value="all">Все типы</option>
            <option value="deposit">Пополнение</option>
            <option value="withdrawal">Вывод</option>
          </select>
        </div>

        {error && (
          <div className="admin-error">
            <p>{error}</p>
            <button onClick={fetchOperations} className="admin-btn admin-btn--primary">
              Попробовать снова
            </button>
          </div>
        )}

        {/* Operations Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Тип</th>
                <th>Пользователь</th>
                <th>Сумма</th>
                <th>Счет</th>
                <th>Статус</th>
                <th>Дата</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {operations.map((operation) => (
                <tr key={operation.id}>
                  <td>{operation.id}</td>
                  <td>
                    <span className={`admin-badge admin-badge--${operation.operation_type}`}>
                      {getTypeText(operation.operation_type)}
                    </span>
                  </td>
                  <td>
                    <div className="admin-user-info">
                      <div className="admin-user-info__name">
                        {operation.first_name} {operation.last_name}
                      </div>
                      <div className="admin-user-info__email">{operation.email}</div>
                    </div>
                  </td>
                  <td className="admin-amount">
                    {formatCurrency(operation.amount, operation.currency)}
                  </td>
                  <td>
                    <div className="admin-account-info">
                      <div className="admin-account-info__number">{operation.number}</div>
                      <div className="admin-account-info__type">
                        {getAccountTypeText(operation.account_type)}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-status admin-status--${getStatusColor(operation.status)}`}>
                      {getStatusText(operation.status)}
                    </span>
                  </td>
                  <td>{formatDate(operation.created_at)}</td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-btn admin-btn--sm admin-btn--info"
                        onClick={() => handleViewDetails(operation)}
                      >
                        Детали
                      </button>
                      
                      <button
                        className="admin-btn admin-btn--sm admin-btn--primary"
                        onClick={() => handleEditOperation(operation)}
                        disabled={processingOperation === operation.id}
                      >
                        Редактировать
                      </button>
                      
                      {operation.operation_type === 'deposit' && operation.status === 'created' && (
                        <button
                          className="admin-btn admin-btn--sm admin-btn--success"
                          onClick={() => handleProcessDeposit(operation.id)}
                          disabled={processingOperation === operation.id}
                        >
                          {processingOperation === operation.id ? 'Обработка...' : 'Обработать'}
                        </button>
                      )}
                      
                      {operation.status === 'created' && (
                        <button
                          className="admin-btn admin-btn--sm admin-btn--warning"
                          onClick={() => handleStatusUpdate(operation.id, 'processing')}
                          disabled={processingOperation === operation.id}
                        >
                          В обработку
                        </button>
                      )}
                      
                      {operation.status === 'processing' && (
                        <>
                          <button
                            className="admin-btn admin-btn--sm admin-btn--success"
                            onClick={() => handleStatusUpdate(operation.id, 'completed')}
                            disabled={processingOperation === operation.id}
                          >
                            Завершить
                          </button>
                          <button
                            className="admin-btn admin-btn--sm admin-btn--danger"
                            onClick={() => handleStatusUpdate(operation.id, 'rejected')}
                            disabled={processingOperation === operation.id}
                          >
                            Отклонить
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination__btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Назад
            </button>
            <span className="admin-pagination__info">
              Страница {currentPage} из {totalPages}
            </span>
            <button
              className="admin-pagination__btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Вперед
            </button>
          </div>
        )}

        {/* Operation Details Modal */}
        {showOperationDetails && selectedOperation && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={() => setShowOperationDetails(false)}></div>
            <div className="admin-modal__content">
              <div className="admin-modal__header">
                <h2>Детали операции #{selectedOperation.id}</h2>
                <button
                  className="admin-modal__close"
                  onClick={() => setShowOperationDetails(false)}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <div className="admin-operation-details">
                  <div className="admin-operation-details__section">
                    <h3>Основная информация</h3>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">ID операции:</span>
                      <span className="admin-detail-value">#{selectedOperation.id}</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Тип:</span>
                      <span className="admin-detail-value">
                        <span className={`admin-badge admin-badge--${selectedOperation.operation_type}`}>
                          {getTypeText(selectedOperation.operation_type)}
                        </span>
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Сумма:</span>
                      <span className="admin-detail-value admin-amount">
                        {formatCurrency(selectedOperation.amount, selectedOperation.currency)}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Статус:</span>
                      <span className="admin-detail-value">
                        <span className={`admin-status admin-status--${getStatusColor(selectedOperation.status)}`}>
                          {getStatusText(selectedOperation.status)}
                        </span>
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Дата создания:</span>
                      <span className="admin-detail-value">{formatDate(selectedOperation.created_at)}</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Дата обновления:</span>
                      <span className="admin-detail-value">{formatDate(selectedOperation.updated_at)}</span>
                    </div>
                  </div>

                  <div className="admin-operation-details__section">
                    <h3>Информация о пользователе</h3>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Имя:</span>
                      <span className="admin-detail-value">
                        {selectedOperation.first_name} {selectedOperation.last_name}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Email:</span>
                      <span className="admin-detail-value">{selectedOperation.email}</span>
                    </div>
                  </div>

                  <div className="admin-operation-details__section">
                    <h3>Информация о счете</h3>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Номер счета:</span>
                      <span className="admin-detail-value">{selectedOperation.account_number}</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Тип счета:</span>
                      <span className="admin-detail-value">
                        {getAccountTypeText(selectedOperation.account_type)}
                      </span>
                    </div>
                  </div>

                  {selectedOperation.comment && (
                    <div className="admin-operation-details__section">
                      <h3>Комментарий пользователя</h3>
                      <div className="admin-comment">
                        {selectedOperation.comment}
                      </div>
                    </div>
                  )}

                  {selectedOperation.admin_comment && (
                    <div className="admin-operation-details__section">
                      <h3>Комментарий администратора</h3>
                      <div className="admin-comment">
                        {selectedOperation.admin_comment}
                      </div>
                    </div>
                  )}

                  {selectedOperation.recipient_details && (
                    <div className="admin-operation-details__section">
                      <h3>Детали получателя</h3>
                      <div className="admin-recipient-details">
                        <pre>{JSON.stringify(selectedOperation.recipient_details, null, 2)}</pre>
                      </div>
                    </div>
                  )}

                  {selectedOperation.contact_method && (
                    <div className="admin-operation-details__section">
                      <h3>Способ связи</h3>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Метод:</span>
                        <span className="admin-detail-value">{selectedOperation.contact_method}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Operation Modal */}
        {showEditModal && editingOperation && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={handleCancelEdit}></div>
            <div className="admin-modal__content admin-modal__content--large">
              <div className="admin-modal__header">
                <h2>Редактирование операции #{editingOperation.id}</h2>
                <button
                  className="admin-modal__close"
                  onClick={handleCancelEdit}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <div className="admin-edit-form">
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__label">Сумма</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.amount}
                        onChange={(e) => handleEditFormChange('amount', e.target.value)}
                        className="admin-edit-form__input"
                        placeholder="Введите сумму"
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__label">Валюта</label>
                      <select
                        value={editForm.currency}
                        onChange={(e) => handleEditFormChange('currency', e.target.value)}
                        className="admin-edit-form__select"
                      >
                        <option value="RUB">RUB</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="CNY">CNY</option>
                      </select>
                    </div>
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__label">Статус</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => handleEditFormChange('status', e.target.value)}
                        className="admin-edit-form__select"
                      >
                        <option value="created">Создана</option>
                        <option value="processing">Обрабатывается</option>
                        <option value="completed">Завершена</option>
                        <option value="rejected">Отклонена</option>
                      </select>
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__label">Комментарий пользователя</label>
                      <textarea
                        value={editForm.comment}
                        onChange={(e) => handleEditFormChange('comment', e.target.value)}
                        className="admin-edit-form__textarea"
                        placeholder="Комментарий пользователя"
                        rows="3"
                      />
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__label">Комментарий администратора</label>
                      <textarea
                        value={editForm.admin_comment}
                        onChange={(e) => handleEditFormChange('admin_comment', e.target.value)}
                        className="admin-edit-form__textarea"
                        placeholder="Комментарий администратора"
                        rows="3"
                      />
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__label">Способ связи</label>
                      <input
                        type="text"
                        value={editForm.contact_method}
                        onChange={(e) => handleEditFormChange('contact_method', e.target.value)}
                        className="admin-edit-form__input"
                        placeholder="Способ связи"
                      />
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field admin-edit-form__field--full">
                      <label className="admin-edit-form__label">Детали получателя (JSON)</label>
                      <textarea
                        value={JSON.stringify(editForm.recipient_details, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            handleEditFormChange('recipient_details', parsed);
                          } catch (error) {
                            // Игнорируем ошибки парсинга JSON во время ввода
                          }
                        }}
                        className="admin-edit-form__textarea"
                        placeholder='{"bankName": "Сбербанк", "number": "1234567890", "accountNumber": "12345678901234567890"}'
                        rows="4"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-modal__footer">
                <button
                  className="admin-btn admin-btn--secondary"
                  onClick={handleCancelEdit}
                >
                  Отмена
                </button>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={handleSaveOperation}
                  disabled={processingOperation === editingOperation.id}
                >
                  {processingOperation === editingOperation.id ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOperations;